// src/services/stripeService.js
import axiosInstance from './api';

/**
 * Stripe/Billing Service
 *
 * Handles all subscription and billing related API calls
 */
const stripeService = {
  /**
   * Create a Stripe Checkout session for subscribing to a plan
   * @param {string} priceId - Stripe price ID (from product configuration)
   * @param {string} orgId - Organization ID
   * @returns {Promise<{sessionId: string, url: string}>} Checkout session details
   */
  createCheckoutSession: async (priceId, orgId) => {
    try {
      const response = await axiosInstance.post('/stripe/create-checkout-session', {
        priceId,
        orgId
      });
      return response.data;
    } catch (error) {
      console.error('[Stripe] Error creating checkout session:', error);
      throw error;
    }
  },

  /**
   * Create a Stripe Customer Portal session for managing subscription
   * @param {string} orgId - Organization ID
   * @returns {Promise<{url: string}>} Portal session URL
   */
  createPortalSession: async (orgId) => {
    try {
      const response = await axiosInstance.post('/stripe/create-portal-session', {
        orgId
      });
      return response.data;
    } catch (error) {
      console.error('[Stripe] Error creating portal session:', error);
      throw error;
    }
  },

  /**
   * Create a Stripe Customer Portal session focused on updating payment method.
   * @param {string} orgId - Organization ID
   * @returns {Promise<{sessionId: string, url: string}>} Portal session details
   */
  createPaymentUpdateSession: async (orgId) => {
    try {
      const response = await axiosInstance.post('/stripe/create-payment-update-session', {
        orgId
      });
      return response.data;
    } catch (error) {
      console.error('[Stripe] Error creating payment update session:', error);
      throw error;
    }
  },

  /**
   * Get current subscription status for the user's organization
   * @returns {Promise<{subscription: object, features: object, type: string}>} Subscription details
   */
  // Billing step 8: each location on this account's bill, its price and why (admins; rate-sheet accounts only).
  getBillingBreakdown: async () => {
    const response = await axiosInstance.get('/stripe/billing-breakdown');
    return response.data;
  },

  getSubscriptionStatus: async () => {
    try {
      const response = await axiosInstance.get('/stripe/subscription-status');
      return response.data;
    } catch (error) {
      console.error('[Stripe] Error fetching subscription status:', error);
      throw error;
    }
  }
};

export default stripeService;
